import axios from 'axios';
import { DataSourceItem, DataSourceRowGroup, DataSourceType } from '../types/DataSource';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface DataSourceQueryParams {
  source: DataSourceType;
  search?: string;
  /** Include retired options. The admin table shows them; the field app never sees them. */
  includeInactive?: boolean;
}

export interface DataSourcePayload {
  source: DataSourceType;
  /**
   * Optional here, required by the server for every family.
   *
   * The page sends only the fields its source actually shows, so the type has to allow a key
   * to be absent; which ones are mandatory is a per-family rule the server owns.
   */
  name?: string | null;
  type?: string | null;
  property?: string | null;
  /** Optional: ROCKFALL options have no code. */
  code?: string | null;
  label?: string | null;
  sortOrder?: number | null;
  isActive?: boolean;
}

class DataSourcesService {
  async getDataSources(params: DataSourceQueryParams): Promise<DataSourceItem[]> {
    const response = await axios.get(`${API_URL}/admin/data-sources`, { params });
    return response.data;
  }

  /**
   * The questions this datasource serves, for the `type` picker.
   *
   * Fetched rather than hardcoded: the server derives it from the datasource definitions, and a
   * type outside that list writes a row no endpoint would ever read.
   */
  async getRowGroups(source: DataSourceType): Promise<DataSourceRowGroup[]> {
    const response = await axios.get(`${API_URL}/admin/data-sources/option-groups`, {
      params: { source },
    });
    return response.data.groups ?? [];
  }

  async createDataSource(payload: DataSourcePayload): Promise<DataSourceItem> {
    const response = await axios.post(`${API_URL}/admin/data-sources`, payload);
    return response.data;
  }

  async updateDataSource(
    id: number,
    payload: Omit<DataSourcePayload, 'source'>
  ): Promise<DataSourceItem> {
    const response = await axios.put(`${API_URL}/admin/data-sources/${id}`, payload);
    return response.data;
  }

  async deleteDataSource(id: number): Promise<void> {
    await axios.delete(`${API_URL}/admin/data-sources/${id}`);
  }
}

export const dataSourcesService = new DataSourcesService();
export default dataSourcesService;
